import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../../CustomHooks/useApi";

const ROLES = ["ALL", "MERCHANT", "DISTRIBUTOR", "MASTER_DISTRIBUTOR"];

const ROLE_STYLE = {
  MERCHANT:           "bg-emerald-100 text-emerald-700",
  DISTRIBUTOR:        "bg-blue-100 text-blue-700",
  MASTER_DISTRIBUTOR: "bg-violet-100 text-violet-700",
};

const FILTER_ACTIVE = {
  ALL:                "bg-gray-800 text-white",
  MERCHANT:           "bg-emerald-600 text-white",
  DISTRIBUTOR:        "bg-blue-600 text-white",
  MASTER_DISTRIBUTOR: "bg-violet-600 text-white",
};

const SearchUser = ({ onSelectUser, checkingUserId, onBulkSelect }) => {
  const [users,       setUsers]       = useState([]);
  const [search,      setSearch]      = useState("");
  const [roleFilter,  setRoleFilter]  = useState("ALL");
  const [expanded,    setExpanded]    = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const { callApi: fetchUsers, loading } = useApi("/admin/users/users-hierarchy/new/v1/not-of-type", "GET");

  useEffect(() => { getUsers(); }, []);

  const getUsers = async () => {
    try {
      const res = await fetchUsers();
      setUsers(Array.isArray(res) ? res : []);
    } catch { setUsers([]); }
  };

  const filtered = useMemo(() => {
    let result = users;
    if (roleFilter !== "ALL") result = result.filter((u) => u.role === roleFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((u) =>
        u?.mobileNumber?.toLowerCase().includes(s) ||
        u?.fullName?.toLowerCase().includes(s) ||
        u?.username?.toLowerCase().includes(s)
      );
    }
    return result;
  }, [search, roleFilter, users]);

  const visible     = filtered;
  const allSelected = visible.length > 0 && visible.every((u) => selectedIds.includes(u.username));
  const selectedUsers = users.filter((u) => selectedIds.includes(u.username));

  const toggleSelect = (username) =>
    setSelectedIds((p) => p.includes(username) ? p.filter((id) => id !== username) : [...p, username]);

  const toggleAll = () => {
    const ids = visible.map((u) => u.username);
    if (allSelected) setSelectedIds((p) => p.filter((id) => !ids.includes(id)));
    else             setSelectedIds((p) => [...new Set([...p, ...ids])]);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">

      {/* Header */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="text-base font-bold text-gray-900">User Lookup</span>
          <span className="bg-gray-100 text-gray-500 px-2.5 py-0.5 rounded-full text-xs font-bold">{users.length}</span>
          {selectedIds.length > 0 && (
            <span className="bg-[#0A2A4F] text-white px-2.5 py-0.5 rounded-full text-xs font-bold">{selectedIds.length} selected</span>
          )}
          {onSelectUser && selectedIds.length === 0 && (
            <span className="text-xs text-blue-500 font-medium">· Click a user to check rates</span>
          )}
        </div>
        <span className="text-gray-400 text-xs font-semibold">{expanded ? "▲ Hide" : "▼ Show"}</span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">

          {/* Search */}
          <input
            type="text"
            placeholder="Search name, mobile, username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition mb-3"
          />

          {/* Role filter pills */}
          <div className="flex gap-2 flex-wrap mb-4">
            {ROLES.map((role) => (
              <button
                key={role}
                onClick={() => setRoleFilter(role)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
                  roleFilter === role ? FILTER_ACTIVE[role] : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {role === "ALL" ? "All Roles" : role.replace(/_/g, " ")}
                {role !== "ALL" && (
                  <span className="ml-1.5 opacity-70">({users.filter((u) => u.role === role).length})</span>
                )}
              </button>
            ))}
          </div>

          {/* Bulk action bar */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 bg-[#0A2A4F]/5 border border-[#0A2A4F]/10 rounded-xl px-4 py-3 mb-3">
              <span className="text-sm font-bold text-[#0A2A4F] flex-1">
                {selectedIds.length} user{selectedIds.length > 1 ? "s" : ""} selected
              </span>
              {onBulkSelect && (
                <button onClick={() => onBulkSelect(selectedUsers)}
                  className="px-4 py-2 rounded-xl bg-[#0A2A4F] text-white text-xs font-bold hover:bg-[#0d3562] transition">
                  Set Rates
                </button>
              )}
              <button onClick={() => setSelectedIds([])}
                className="px-3 py-2 rounded-xl bg-white border border-gray-200 text-xs text-gray-500 font-medium hover:bg-gray-50">
                Clear
              </button>
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center">
              <div className="w-6 h-6 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-400">Loading users…</p>
            </div>
          ) : (
            <>
              {/* Select all */}
              {visible.length > 0 && (
                <label className="flex items-center gap-3 px-4 py-2.5 mb-2 bg-gray-50 rounded-xl cursor-pointer">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-[#0A2A4F] rounded" />
                  <span className="text-xs font-semibold text-gray-500">Select all ({visible.length})</span>
                </label>
              )}

              <div className="max-h-72 overflow-y-auto space-y-1.5 scrollbar-hide">
                {visible.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No users found</p>
                ) : (
                  visible.map((user) => {
                    const isChecking = checkingUserId === user.username;
                    const isSelected = selectedIds.includes(user.username);
                    return (
                      <div
                        key={user.id || user.username}
                        className={`flex items-center gap-3 rounded-xl px-4 py-3 border transition ${
                          isSelected   ? "border-[#0A2A4F]/20 bg-[#0A2A4F]/5" :
                          isChecking   ? "border-blue-200 bg-blue-50 cursor-wait" :
                          "border-gray-100 hover:bg-gray-50"
                        }`}
                      >
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(user.username)}
                          onClick={(e) => e.stopPropagation()} className="w-4 h-4 accent-[#0A2A4F] rounded flex-shrink-0" />

                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => onSelectUser && !checkingUserId && onSelectUser(user)}
                        >
                          <p className="font-bold text-sm text-gray-800 truncate">{user.fullName || user.username}</p>
                          <p className="text-xs text-gray-400">{user.mobileNumber} · {user.username}</p>
                        </div>

                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0 ${ROLE_STYLE[user.role] || "bg-gray-100 text-gray-500"}`}>
                          {user.role?.replace(/_/g, " ")}
                        </span>

                        {onSelectUser && (
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-lg flex-shrink-0 ${isChecking ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-400"}`}>
                            {isChecking ? "Checking…" : "Check Rates"}
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchUser;
